import { getErrorMessage } from "@/lib/error-message";
import { axiosInstance } from "@/lib/axios";
import { useAuthStore } from "@/store/zustand/useAuthStore";
import {
  createUseAuthMutations,
  createUseGetCurrentUserQuery,
  useVerifyMfaMutation as useSharedVerifyMfaMutation,
} from "@membentry/shared-hooks";
import { createAccountApi } from "@membentry/shared-api";
import type { UserDetailDTO } from "@membentry/shared-types";
import { verifyMfaCode } from "../auth";
import { toast } from "sonner";

const accountApi = createAccountApi(axiosInstance);

export const useAuthMutations = () => {
  const authStore = useAuthStore();

  const { loginMutation, registerMutation } = createUseAuthMutations({
    authStore: {
      setTokens: authStore.setTokens,
      setUserData: authStore.setUserData,
      clearAuthState: authStore.clearAuthState,
      setRequiresMfaSetup: authStore.setRequiresMfaSetup,
    },
    accountApi,
    onLoginError: () => undefined,
    onError: (error) => {
      const message = getErrorMessage(error, "Login failed");
      if (message.includes("locked") || message.includes("lockout")) {
        toast.error(
          "Account is locked. Please try again later or contact support.",
        );
      } else {
        toast.error(message);
      }
    },
  });

  const mfaVerifyMutation = useSharedVerifyMfaMutation(({ email, code }) =>
    verifyMfaCode(email, code), {
    mutationKey: ["mfa-verify"],
    onSuccess: (data) => {
      authStore.setTokens(data.accessToken, data.refreshToken);
      authStore.setUserData(data.user);
      toast.success("MFA verification successful");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "MFA verification failed"));
    },
  });

  return {
    setTokens: authStore.setTokens,
    setUserData: authStore.setUserData,
    clearAuthState: authStore.clearAuthState,
    loginMutation,
    mfaVerifyMutation,
    registerMutation,
  };
};

export const useGetCurrentUserQuery = () => createUseGetCurrentUserQuery(accountApi);
